// Capa HTTP y lógica pura del Laboratorio de IA (`/admin/ia/laboratorio`):
// prompts con borrador, casos de prueba congelados, experimentos y el registro
// de corridas. El estado vive en TanStack Query (`hooks/useLaboratorio.ts`); acá
// van los tipos del contrato (8a), los fetch y las reglas que se prueban sin
// montar nada: la puerta de publicación, los resúmenes, los diffs y los filtros.

import { apiFetch } from "./api";
import { formatearFechaYHora } from "./fechas";

/* ---------------------------------------------------------------------------
   Contrato del API (8a)
--------------------------------------------------------------------------- */

export interface PromptDelLaboratorio {
  id: number;
  codigo: string;
  nombre: string;
  fase: string;
  revision_en_uso_id: number | null;
  version_publicada: string | null;
  tiene_borrador: boolean;
  borrador_revision_id: number | null;
  borrador_texto: string | null;
  proveedor: string;
  modelo: string;
  temperatura: number | null;
  admite_experimentos: boolean;
  uso: { llamadas: number; corridas: number; informes: number };
}

export interface CatalogoDePrompts {
  data: PromptDelLaboratorio[];
  fases: string[];
}

export interface RevisionDelPrompt {
  id: number;
  autor_tipo: "persona" | "ia" | "sistema";
  nombre: string | null;
  creada_en: string | null;
  en_uso: boolean;
  cuerpo?: string;
}

export interface ResumenDelMes {
  desde: string;
  costo_usd: number;
  llamadas: number;
  con_error: number;
  porcentaje_con_error: number;
}

export interface CasoDeLaboratorio {
  id: number;
  nombre: string;
  etiqueta: string | null;
  informe: { id: number | null; numero: string | null } | null;
  corrida_id: number | null;
  hechos: number;
  documentos: number;
  dudas: number;
  tiene_texto_real: boolean;
  congelado_en: string;
  creado_por: string | null;
}

export interface ResumenDeLado {
  bueno: number;
  regular: number;
  malo: number;
  sin_calificar: number;
  chequeos_ok: number;
  chequeos_total: number;
  resultados: number;
}

export interface ResumenDelExperimento {
  candidata: ResumenDeLado;
  publicada: ResumenDeLado;
  aprobado: boolean;
  /** Falso si el borrador se siguió editando: el experimento probó otro texto. */
  texto_actual: boolean;
}

export type EstadoDelExperimento = "corriendo" | "terminado";

export interface ExperimentoResumido {
  id: number;
  funcion: { id: number; codigo: string; nombre: string };
  revision_id: number;
  item_codigo: string;
  estado: EstadoDelExperimento;
  iniciado_en: string;
  terminado_en: string | null;
  casos: number;
  resumen: ResumenDelExperimento;
}

export type Calificacion = "bueno" | "regular" | "malo";

export interface Chequeo {
  codigo: string;
  ok: boolean;
  etiqueta: string;
  detalle: string | null;
}

export interface ResultadoDelExperimento {
  id: number;
  estado: "pendiente" | "listo" | "error";
  texto: string | null;
  palabras: number | null;
  tokens_entrada: number | null;
  tokens_salida: number | null;
  costo_usd: string | null;
  latencia_ms: number | null;
  chequeos: Chequeo[];
  calificacion: Calificacion | null;
  comentario: string | null;
  codigo_error: string | null;
}

export interface CasoDelExperimento {
  caso_id: number | null;
  nombre: string;
  etiqueta: string | null;
  informe_numero: string | null;
  candidata: ResultadoDelExperimento | null;
  publicada: ResultadoDelExperimento | null;
  real: { texto: string | null };
}

export interface VersionDelExperimento {
  revision_id: number;
  etiqueta: string;
  es_borrador: boolean;
  en_uso: boolean;
}

export interface ExperimentoCompleto {
  id: number;
  funcion: { id: number; codigo: string; nombre: string };
  item_codigo: string;
  estado: EstadoDelExperimento;
  candidata: VersionDelExperimento | null;
  publicada: VersionDelExperimento | null;
  progreso: { total: number; listos: number; errores: number };
  costo_usd: string | null;
  iniciado_en: string;
  terminado_en: string | null;
  casos: CasoDelExperimento[];
  resumen: ResumenDelExperimento;
}

export interface NuevoExperimento {
  funcion: number;
  item_codigo?: string;
  casos: number[];
}

export type EstadoDeLlamada = "ok" | "warn" | "err";

export interface CorridaDeLaLista {
  id: number;
  informe: { id: number; numero: string | null } | null;
  motivo: string;
  estado: string;
  iniciada_en: string;
  terminada_en: string | null;
  duracion_s: number | null;
  llamadas: number;
  errores: number;
  costo_usd: string | null;
}

export interface PaginaDeCorridas {
  data: CorridaDeLaLista[];
  meta: { total: number; pagina: number; por_pagina: number; ultima_pagina: number; llamadas_en_pagina: number };
}

export interface LlamadaDelPaso {
  id: number;
  nombre: string;
  estado: EstadoDeLlamada;
  duracion_ms: number | null;
}

export interface PasoDeLaCorrida {
  id: number | null;
  nombre: string | null;
  estado: string | null;
  duracion_ms: number | null;
  resumen: { llamadas: number; duracion_ms: number };
  llamadas: LlamadaDelPaso[];
}

export interface CorridaCompleta {
  id: number;
  informe: { id: number; numero: string | null } | null;
  motivo: string;
  estado: string;
  codigo_error: string | null;
  iniciada_en: string;
  terminada_en: string | null;
  tokens_entrada: number | null;
  tokens_salida: number | null;
  costo_usd: string | null;
  pasos: PasoDeLaCorrida[];
}

export interface PayloadDeLlamada {
  texto: string;
  truncado: boolean;
}

export interface DetalleDeLlamada {
  id: number;
  estado: EstadoDeLlamada;
  funcion: { id: number; codigo: string; nombre: string } | null;
  revision: { id: number; etiqueta: string } | null;
  corrida: {
    id: number;
    motivo: string;
    informe: { id: number; numero: string | null } | null;
    paso: string | null;
  };
  proveedor: string | null;
  modelo: string | null;
  temperatura: number | null;
  tokens_entrada: number | null;
  tokens_salida: number | null;
  costo_usd: string | null;
  latencia_ms: number | null;
  finish_reason: string | null;
  iniciada_en: string;
  error: string | null;
  herramientas: {
    nombre: string | null;
    estado: string | null;
    argumentos: unknown;
    resultado: unknown;
    tiempo_ms: number | null;
  }[];
  entrada: PayloadDeLlamada | null;
  salida: PayloadDeLlamada | null;
}

export type EstadoFiltro = "cualquiera" | "con_error" | "sin_error";

export interface FiltrosDeCorridas {
  informe: string;
  funcion: string;
  revision: string;
  estado: EstadoFiltro;
}

/* ---------------------------------------------------------------------------
   Fetch
--------------------------------------------------------------------------- */

const BASE = "/api/ia/laboratorio";

export const fetchCatalogoDePrompts = () => apiFetch<CatalogoDePrompts>(`${BASE}/prompts`);

export async function fetchResumenDelMes(): Promise<ResumenDelMes> {
  return (await apiFetch<{ data: ResumenDelMes }>(`${BASE}/resumen`)).data;
}

export async function fetchRevisionesDelPrompt(funcionId: number): Promise<RevisionDelPrompt[]> {
  return (await apiFetch<{ data: RevisionDelPrompt[] }>(`/api/ia/funciones/${funcionId}/revisiones`)).data;
}

export async function fetchRevisionDelPrompt(funcionId: number, revisionId: number): Promise<RevisionDelPrompt> {
  return (await apiFetch<{ data: RevisionDelPrompt }>(`/api/ia/funciones/${funcionId}/revisiones/${revisionId}`)).data;
}

export async function guardarBorradorRequest(funcionId: number, texto: string): Promise<PromptDelLaboratorio> {
  return (
    await apiFetch<{ data: PromptDelLaboratorio }>(`${BASE}/prompts/${funcionId}/borrador`, {
      method: "PUT",
      body: JSON.stringify({ texto }),
    })
  ).data;
}

export async function publicarPromptRequest(funcionId: number): Promise<PromptDelLaboratorio> {
  return (
    await apiFetch<{ data: PromptDelLaboratorio }>(`${BASE}/prompts/${funcionId}/publicar`, {
      method: "POST",
      body: JSON.stringify({}),
    })
  ).data;
}

export async function fetchCasos(): Promise<CasoDeLaboratorio[]> {
  return (await apiFetch<{ data: CasoDeLaboratorio[] }>(`${BASE}/casos`)).data;
}

export interface NuevoCaso {
  nombre: string;
  etiqueta?: string | null;
  informe_id?: number;
  corrida_id?: number;
}

export async function crearCasoRequest(input: NuevoCaso): Promise<CasoDeLaboratorio> {
  return (
    await apiFetch<{ data: CasoDeLaboratorio }>(`${BASE}/casos`, { method: "POST", body: JSON.stringify(input) })
  ).data;
}

export function borrarCasoRequest(id: number): Promise<void> {
  return apiFetch<void>(`${BASE}/casos/${id}`, { method: "DELETE" });
}

export async function fetchExperimentos(funcionId?: number): Promise<ExperimentoResumido[]> {
  const consulta = funcionId ? `?funcion=${funcionId}` : "";
  return (await apiFetch<{ data: ExperimentoResumido[] }>(`${BASE}/experimentos${consulta}`)).data;
}

export async function fetchExperimento(id: number): Promise<ExperimentoCompleto> {
  return (await apiFetch<{ data: ExperimentoCompleto }>(`${BASE}/experimentos/${id}`)).data;
}

export async function crearExperimentoRequest(input: NuevoExperimento): Promise<ExperimentoCompleto> {
  return (
    await apiFetch<{ data: ExperimentoCompleto }>(`${BASE}/experimentos`, {
      method: "POST",
      body: JSON.stringify(input),
    })
  ).data;
}

export async function volverACorrerRequest(id: number): Promise<ExperimentoCompleto> {
  return (
    await apiFetch<{ data: ExperimentoCompleto }>(`${BASE}/experimentos/${id}/volver-a-correr`, {
      method: "POST",
      body: JSON.stringify({}),
    })
  ).data;
}

export function calificarRequest(
  experimentoId: number,
  resultadoId: number,
  calificacion: Calificacion,
  comentario: string | null,
): Promise<{ data: { resultado: ResultadoDelExperimento; resumen: ResumenDelExperimento } }> {
  return apiFetch(`${BASE}/experimentos/${experimentoId}/resultados/${resultadoId}/calificacion`, {
    method: "PUT",
    body: JSON.stringify({ calificacion, comentario }),
  });
}

export function fetchCorridas(filtros: FiltrosDeCorridas, pagina = 1): Promise<PaginaDeCorridas> {
  const consulta = parametrosDeCorridas(filtros, pagina);
  return apiFetch<PaginaDeCorridas>(`/api/ia/corridas${consulta ? `?${consulta}` : ""}`);
}

export async function fetchCorrida(id: number): Promise<CorridaCompleta> {
  return (await apiFetch<{ data: CorridaCompleta }>(`/api/ia/corridas/${id}`)).data;
}

export async function fetchLlamada(id: number): Promise<DetalleDeLlamada> {
  return (await apiFetch<{ data: DetalleDeLlamada }>(`/api/ia/llamadas/${id}`)).data;
}

/* ---------------------------------------------------------------------------
   Prompts
--------------------------------------------------------------------------- */

export function agruparPorFase(
  prompts: PromptDelLaboratorio[],
  fases: string[],
): { fase: string; prompts: PromptDelLaboratorio[] }[] {
  const orden = [...fases, ...prompts.map((p) => p.fase).filter((f) => !fases.includes(f))];

  return [...new Set(orden)]
    .map((fase) => ({ fase, prompts: prompts.filter((p) => p.fase === fase) }))
    .filter((g) => g.prompts.length > 0);
}

/** «v3»: la posición entre las revisiones de la función, de la más vieja a la más nueva. */
export function etiquetaDeRevision(revisiones: { id: number }[], id: number): string | null {
  const ordenadas = [...revisiones].sort((a, b) => a.id - b.id);
  const posicion = ordenadas.findIndex((r) => r.id === id);

  return posicion === -1 ? null : `v${posicion + 1}`;
}

export type TipoDeGate = "sin_experimentos_en_la_funcion" | "publicada" | "sin_experimento" | "pendiente" | "aprobado";

export interface EstadoDeLaPuerta {
  tipo: TipoDeGate;
  mensaje: string;
  puedePublicar: boolean;
}

const plural = (n: number, uno: string, varios: string) => (n === 1 ? uno : varios);

/**
 * Lo que dice la banda de la puerta de publicación, a partir del último
 * experimento del borrador. El servidor es quien decide (409 al publicar): esto
 * solo lo anticipa con las mismas razones.
 */
export function estadoDeLaPuerta(
  prompt: PromptDelLaboratorio,
  experimento: ExperimentoResumido | null,
  etiquetaDelBorrador: string | null,
): EstadoDeLaPuerta {
  if (!prompt.admite_experimentos) {
    return { tipo: "sin_experimentos_en_la_funcion", mensaje: "Sin experimentos para esta función.", puedePublicar: false };
  }

  if (!prompt.tiene_borrador) {
    return {
      tipo: "publicada",
      mensaje: `${prompt.version_publicada ?? "La versión"} publicada. Se usará en las próximas corridas.`,
      puedePublicar: false,
    };
  }

  if (experimento === null) {
    return { tipo: "sin_experimento", mensaje: "Sin experimentos recientes para el borrador.", puedePublicar: false };
  }

  const { candidata, aprobado, texto_actual } = experimento.resumen;

  if (experimento.estado === "corriendo") {
    return { tipo: "pendiente", mensaje: "El experimento todavía está corriendo.", puedePublicar: false };
  }

  if (!texto_actual) {
    return {
      tipo: "pendiente",
      mensaje: "El borrador cambió después del experimento: vuelve a correrlo para publicar este texto.",
      puedePublicar: false,
    };
  }

  if (aprobado) {
    return {
      tipo: "aprobado",
      mensaje: `El borrador pasó el experimento en ${candidata.resultados} de ${candidata.resultados} casos. Ya se puede publicar como ${etiquetaDelBorrador ?? "la versión siguiente"}.`,
      puedePublicar: true,
    };
  }

  if (candidata.malo > 0) {
    return {
      tipo: "pendiente",
      mensaje: `El borrador tiene ${candidata.malo} ${plural(candidata.malo, "caso calificado como malo", "casos calificados como malos")}: corrígelo y vuelve a probarlo.`,
      puedePublicar: false,
    };
  }

  if (candidata.sin_calificar > 0) {
    return {
      tipo: "pendiente",
      mensaje: `Para publicar, el borrador debe pasar un experimento: falta calificar ${candidata.sin_calificar} de ${candidata.resultados} casos.`,
      puedePublicar: false,
    };
  }

  return {
    tipo: "pendiente",
    mensaje: "El experimento no se pudo completar en todos los casos: vuelve a correrlo.",
    puedePublicar: false,
  };
}

/* ---------------------------------------------------------------------------
   Experimentos
--------------------------------------------------------------------------- */

export function resumenDelLado(lado: ResumenDeLado): string {
  return `${lado.bueno} bueno · ${lado.regular} regular · ${lado.malo} malo · chequeos ${lado.chequeos_ok}/${lado.chequeos_total}`;
}

export function veredictoDelExperimento(resumen: ResumenDelExperimento): {
  tono: "success" | "warning" | "danger";
  texto: string;
} {
  const { candidata } = resumen;

  if (resumen.aprobado) return { tono: "success", texto: "El borrador se puede publicar" };
  if (candidata.malo > 0) return { tono: "danger", texto: "El borrador tiene casos calificados como malos" };
  if (candidata.sin_calificar > 0) {
    return { tono: "warning", texto: `Califica el borrador en los ${candidata.resultados} casos para decidir` };
  }

  return { tono: "warning", texto: "Vuelve a correr el experimento para decidir" };
}

/** Las citas `[[valor|clave]]` se leen como su valor. */
export function textoLegible(texto: string): string {
  return texto.replace(/\[\[(.*?)\|[^\]|]*\]\]/g, "$1");
}

export function parrafosDelTexto(texto: string | null): string[] {
  if (!texto) return [];

  return textoLegible(texto)
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p !== "");
}

export interface TramoDePalabras {
  texto: string;
  marca: boolean;
}

/** Posiciones (índices en `a`) de las palabras que pertenecen a la subsecuencia común más larga con `b`. */
function palabrasComunes(a: string[], b: string[]): { enA: Set<number>; enB: Set<number> } {
  const dp: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));

  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      dp[i]![j] = a[i] === b[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }

  const enA = new Set<number>();
  const enB = new Set<number>();
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      enA.add(i);
      enB.add(j);
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) i++;
    else j++;
  }

  return { enA, enB };
}

function tramos(texto: string, comunes: Set<number> | null): TramoDePalabras[] {
  let palabra = -1;

  return texto
    .split(/(\s+)/)
    .filter((t) => t !== "")
    .map((t) => {
      if (/^\s+$/.test(t)) return { texto: t, marca: false };
      palabra++;

      return { texto: t, marca: comunes !== null && !comunes.has(palabra) };
    });
}

/**
 * Compara un párrafo del borrador con el de la versión publicada, palabra por
 * palabra: marca lo del borrador que la publicada no tiene y lo de la publicada
 * que el borrador ya no tiene. Sin el otro lado no marca nada.
 */
export function compararParrafos(
  borrador: string,
  publicada: string | null,
): { borrador: TramoDePalabras[]; publicada: TramoDePalabras[] } {
  if (publicada === null) return { borrador: tramos(borrador, null), publicada: [] };

  const palabras = (t: string) => t.split(/\s+/).filter((p) => p !== "");
  const { enA, enB } = palabrasComunes(palabras(borrador), palabras(publicada));

  return { borrador: tramos(borrador, enA), publicada: tramos(publicada, enB) };
}

/* ---------------------------------------------------------------------------
   Diff de líneas (borrador contra publicada)
--------------------------------------------------------------------------- */

export interface LineaDelDiff {
  tipo: "igual" | "agregada" | "quitada";
  texto: string;
}

export function diffDeLineas(anterior: string, nuevo: string): LineaDelDiff[] {
  const a = anterior.split("\n");
  const b = nuevo.split("\n");
  const dp: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));

  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      dp[i]![j] = a[i] === b[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }

  const resultado: LineaDelDiff[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      resultado.push({ tipo: "igual", texto: a[i]! });
      i++;
      j++;
    } else if (j >= b.length || (i < a.length && dp[i + 1]![j]! >= dp[i]![j + 1]!)) {
      resultado.push({ tipo: "quitada", texto: a[i]! });
      i++;
    } else {
      resultado.push({ tipo: "agregada", texto: b[j]! });
      j++;
    }
  }

  return resultado;
}

/* ---------------------------------------------------------------------------
   Formatos y filtros
--------------------------------------------------------------------------- */

const ES = "es-CL";

export function formatearDuracion(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 60000) return `${(ms / 1000).toLocaleString(ES, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;

  return `${Math.round(ms / 60000)} min`;
}

export function formatearPorcentajeDeError(valor: number): string {
  return `${valor.toLocaleString(ES, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

/** «14-10 · 09:12», en hora de Chile. */
export function fechaCortaDeLaCorrida(iso: string): string {
  const completa = formatearFechaYHora(iso);

  return completa === "—" ? completa : `${completa.slice(0, 5)} · ${completa.slice(11)}`;
}

export function estadoDeLaLlamada(estado: EstadoDeLlamada): { etiqueta: string; tono: "success" | "warning" | "danger" } {
  if (estado === "ok") return { etiqueta: "Correcta", tono: "success" };
  if (estado === "warn") return { etiqueta: "Con observación", tono: "warning" };

  return { etiqueta: "Error", tono: "danger" };
}

/** Solo lo que la persona eligió: un parámetro vacío haría fallar la validación del API. */
export function parametrosDeCorridas(filtros: FiltrosDeCorridas, pagina = 1): string {
  const p = new URLSearchParams();
  if (filtros.informe) p.set("informe_id", filtros.informe);
  if (filtros.funcion) p.set("funcion_id", filtros.funcion);
  if (filtros.revision) p.set("revision_id", filtros.revision);
  if (filtros.estado !== "cualquiera") p.set("estado", filtros.estado);
  if (pagina > 1) p.set("page", String(pagina));

  return p.toString();
}

export const TABS_DEL_LABORATORIO = ["prompts", "casos", "experimentos", "corridas"] as const;
export type TabDelLaboratorio = (typeof TABS_DEL_LABORATORIO)[number];

const TONOS_DE_ETIQUETA = ["success", "warning", "info"] as const;

/**
 * El tono de la etiqueta libre de un caso («Frutícola», «Secano»…). La elige la
 * persona, así que no hay un mapa: se reparte entre tres tonos con un hash
 * estable para que la misma etiqueta se vea siempre igual.
 */
export function tonoDeLaEtiquetaDelCaso(etiqueta: string | null): (typeof TONOS_DE_ETIQUETA)[number] | null {
  const limpia = etiqueta?.trim().toLowerCase() ?? "";
  if (limpia === "") return null;

  let suma = 0;
  for (const c of limpia) suma = (suma * 31 + c.charCodeAt(0)) % 997;

  return TONOS_DE_ETIQUETA[suma % TONOS_DE_ETIQUETA.length]!;
}

/** La pestaña vive en el hash de la url (`#casos`); lo desconocido cae en la primera. */
export function tabDelLaboratorio(hash: string): TabDelLaboratorio {
  const valor = hash.replace(/^#/, "");

  return (TABS_DEL_LABORATORIO as readonly string[]).includes(valor) ? (valor as TabDelLaboratorio) : "prompts";
}
